import { useEffect, useState } from 'react';
import { useEventCallback } from './use-event-callback';

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  onObserved: (entry: IntersectionObserverEntry) => void;
  triggerOnce?: boolean;
}

export function useIntersectionObserver<T extends HTMLElement>({
  onObserved,
  triggerOnce,
  root,
  rootMargin,
  threshold,
}: UseIntersectionObserverOptions) {
  const [node, setNode] = useState<T | null>(null);

  const eventCallback = useEventCallback(([entry]: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    if (entry.isIntersecting && triggerOnce && node) {
      onObserved(entry);
      return observer.unobserve(node);
    }

    if (entry.isIntersecting) {
      onObserved(entry);
    }
  });

  useEffect(() => {
    const observer = new IntersectionObserver(eventCallback, { root, rootMargin, threshold });

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [eventCallback, node, root, rootMargin, threshold]);

  return (element: T | null) => {
    setNode(element);
  };
}
